import React, { useEffect, useState } from "react";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Box,
  Button,
  Link as ExternalLink,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  Link,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from "react-router-dom";

import { Asset_ExistsResult } from "@safetyculture/s12-apis-connect-web/s12/iot/v1/asset_pb";

import { UrlParamTypes } from "../types/org-display";
import {
  SensorIndexToTab,
  SensorTabType,
  getTabIndex,
  routeSlugToSensorTab,
  sensorTabList,
  sensorTabToDisplayName,
  sensorTabToRouteSlug,
} from "components/sensors/routes/tabs";
import { LocationState } from "components/sensors/types";
import OrganisationSensorsTable from "components/sensors/views/active-sensors";
import OrganisationSensorAlerts from "components/sensors/views/alerts";
import OrganisationArchivedSensorsTable from "components/sensors/views/archive";
import OrganisationGatewaysTable from "components/sensors/views/gateways";

export function displaySensorDetails(
  existsResult: Asset_ExistsResult,
): boolean {
  return (
    existsResult == Asset_ExistsResult.ASSET_ACTIVE_ASSET_EXISTS ||
    existsResult == Asset_ExistsResult.ASSET_ARCHIVED_ASSET_EXISTS
  );
}

const OrganisationSensors = () => {
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { regionCode, roleId } = useParams<UrlParamTypes>();
  const { orgName } = location.state || { orgName: "" };

  const tabMatch = useRouteMatch<{ tab?: string }>(
    "/organisation/:regionCode/:roleId/sensors/:tab?",
  );

  const [activeTab, setActiveTab] = useState<SensorTabType>(
    routeSlugToSensorTab(tabMatch?.params.tab),
  );

  // Keep activeTab in sync when the URL changes underneath us (e.g. browser
  // back/forward, or a direct link to a specific tab).
  useEffect(() => {
    setActiveTab(routeSlugToSensorTab(tabMatch?.params.tab));
  }, [tabMatch?.params.tab]);

  const navigateToTab = (newTabType: SensorTabType) => {
    setActiveTab(newTabType);
    const slug = sensorTabToRouteSlug[newTabType];
    history.push({
      pathname: `/organisation/${regionCode}/${roleId}/sensors${slug ? `/${slug}` : ""}`,
      state: location.state,
    });
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    const newTabType = SensorIndexToTab[newValue];
    if (newTabType) {
      navigateToTab(newTabType);
    }
  };

  const renderTab = (tabType: SensorTabType) => {
    switch (tabType) {
      case SensorTabType.SENSORS:
        return <OrganisationSensorsTable />;
      case SensorTabType.ARCHIVED:
        return <OrganisationArchivedSensorsTable />;
      case SensorTabType.GATEWAYS:
        return <OrganisationGatewaysTable />;
      case SensorTabType.ALERTS:
        return (
          <OrganisationSensorAlerts regionCode={regionCode} roleId={roleId} />
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12} style={{ paddingBottom: "8px" }}>
          <Grid
            container
            alignItems="center"
            spacing={1}
            sx={{ marginBottom: 2 }}
          >
            <Grid item>
              <Link
                to={`/organisation/${regionCode}/${roleId}`}
                className="remove-link-style"
              >
                <Button startIcon={<ArrowBackIcon />} color="primary">
                  Back
                </Button>
              </Link>
            </Grid>

            <Grid item xs>
              <Typography
                variant="h5"
                color="textPrimary"
                data-testid="organisation-history-title"
              >
                {orgName
                  ? `${orgName} ${sensorTabToDisplayName[activeTab].toLowerCase()}`
                  : `Organisation ${sensorTabToDisplayName[activeTab]}`}
              </Typography>
            </Grid>

            <Grid item>
              <ExternalLink
                href="https://help.safetyculture.com/en-US/000107/"
                target="_blank"
                color="primary"
                rel="noopener noreferrer"
                variant="caption"
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                Hardware troubleshooting
                <LaunchIcon sx={{ fontSize: "1em" }} />
              </ExternalLink>
            </Grid>
          </Grid>
          <Tabs
            value={getTabIndex(activeTab)}
            onChange={handleTabChange}
            aria-label="Sensor management tabs"
          >
            {sensorTabList.map((tabType) => (
              <Tab
                key={tabType}
                label={sensorTabToDisplayName[tabType]}
                value={getTabIndex(tabType)}
              />
            ))}
          </Tabs>

          <Box sx={{ marginTop: 2 }}>{renderTab(activeTab)}</Box>
        </Grid>
      </Grid>
    </>
  );
};
export default OrganisationSensors;
